import React, {useContext, useState} from "react";
import "./Creation.css";
import {useNavigate} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {
  GroupCreationResponse,
  GroupDetailResponse,
  GroupMemberRequest,
} from "../../../logic/APITypes";
import API, {Resolution} from "../../../logic/API";
import {TextArea, TextInput} from "@spg/component_library";
import {SnackbarContext} from "../../../utility/SnackbarProvider";
import GroupMailInputs from "./MailInputs";

type GroupCreationEditing = {
  prefill: GroupDetailResponse & {groupId: string};
  updateGroup: (name: string, description: string) => void;
};

/** A tile element of the apps list containing overview information of installed applications */
function GroupCreation({editing}: {editing?: GroupCreationEditing}) {
  const {formatMessage} = useIntl();
  const navigate = useNavigate();
  const {addSnackbar} = useContext(SnackbarContext);
  const [name, setName] = useState(editing?.prefill?.name ?? "");
  const [slug, setSlug] = useState(editing?.prefill?.slug ?? "");
  const [description, setDescription] = useState(
    editing?.prefill?.description ?? "",
  );
  const [admins, setAdmins] = useState<GroupMemberRequest[]>([
    {email: "", is_admin: true},
  ]);
  const [resolution, setResolution] =
    useState<Resolution<GroupCreationResponse>>();

  return (
    <div className="groupCreation">
      <h1>
        {editing?.prefill?.name || (
          <FormattedMessage id="groupCreation.header.page" />
        )}
      </h1>
      {!editing && (
        <p className="groupCreation__paragraph">
          <FormattedMessage
            id="groupCreation.paragraph"
            values={{br: () => <br />}}
          />
        </p>
      )}
      <div className="groupCreation__row">
        <TextInput
          label={formatMessage({id: "groupCreation.input.name"})}
          id="groupCreationName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={resolution?.unresolved}
        />
        {!editing && (
          <TextInput
            label={formatMessage({id: "groupCreation.input.slug"})}
            id="groupCreationSlug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            disabled={resolution?.unresolved}
          />
        )}
      </div>
      <TextArea
        label={formatMessage({id: "groupCreation.input.description"})}
        id="groupCreationDescription"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={resolution?.unresolved}
        rows={6}
      />
      {!editing && (
        <>
          <h2>
            <FormattedMessage id="groupCreation.header.admins" />
          </h2>
          <GroupMailInputs
            invitations={{
              get: admins,
              set: setAdmins,
            }}
            adminsOnly={true}
            disabled={resolution?.unresolved}
          />
        </>
      )}
      <div className="groupCreation__buttons">
        {!!editing && (
          <button
            id="cancel_button"
            disabled={resolution?.unresolved}
            className="linkButton"
            onClick={() => {
              editing.updateGroup(
                editing.prefill.name,
                editing.prefill.description,
              );
            }}
          >
            <FormattedMessage id="groupCreation.button.cancel" />
          </button>
        )}
        <button
          id="submit_button"
          type="submit"
          disabled={
            resolution?.unresolved ||
            (!editing && slug.trim().length === 0) ||
            name.trim().length === 0
          }
          className="linkButton"
          onClick={() => {
            setResolution({success: false, unresolved: true});
            if (editing)
              API.updateGroup(
                editing.prefill.groupId,
                name.trim(),
                description.trim(),
              ).then((response) => {
                if (!response.success) {
                  addSnackbar({
                    variant: "close",
                    text: formatMessage({
                      id: "groupCreation.snackbar.edit.error",
                    }),
                    hasCloseButton: true,
                  });
                  setResolution({success: false});
                } else editing.updateGroup(name, description);
              });
            else
              API.createGroup(
                name.trim(),
                slug.trim(),
                description.trim(),
                admins
                  .map((admin) => admin.email.trim())
                  .filter((mail) => mail.length > 0),
              ).then((response) => {
                if (!response.payload) {
                  addSnackbar({
                    variant: "close",
                    text: formatMessage({
                      id: "groupCreation.snackbar.create.error",
                    }),
                    hasCloseButton: true,
                  });
                  setResolution({success: false});
                } else navigate("/groups/" + response.payload.id);
              });
          }}
        >
          <FormattedMessage
            id={"groupCreation.button." + (editing ? "edit" : "create")}
          />
        </button>
      </div>
    </div>
  );
}

export default GroupCreation;
